import {
  RetainerFeeBreakdownType,
  RetainerFeeType,
  RetainerWorkType,
  type CreateRetainerRequestInput,
} from '~/gql/graphql';
import {
  BILLING_TYPE_FLAT,
  WORK_TYPE_OPTIONS,
  type RequestRetainerValidatedFormData,
} from '~/modules/matters/modals/requestRetainerSchema';
import { CENTS_PER_DOLLAR } from '~/shared/constants';

function resolveFeeBreakdownTypeFromFormFee(
  feeType: '' | RetainerFeeBreakdownType,
): RetainerFeeBreakdownType {
  if (feeType === '') {
    return RetainerFeeBreakdownType.PrivateRetainer;
  }
  return feeType;
}

function resolveFeeBreakdownType(
  formData: RequestRetainerValidatedFormData,
): RetainerFeeBreakdownType {
  return resolveFeeBreakdownTypeFromFormFee(formData.feeType);
}

function getWorkDescription(
  formData: RequestRetainerValidatedFormData,
): string {
  if (formData.workType === RetainerWorkType.SomethingElse) {
    return (formData.workTypeOtherComment ?? '').trim();
  }
  return '';
}

function getWorkTypeLabel(formData: RequestRetainerValidatedFormData): string {
  if (formData.workType === RetainerWorkType.SomethingElse) {
    return getWorkDescription(formData);
  }
  return (
    WORK_TYPE_OPTIONS.find((option) => option.value === formData.workType)
      ?.label ?? ''
  );
}

function buildAdditionalDetailsForHourly(
  formData: RequestRetainerValidatedFormData,
  workType: RetainerWorkType,
): string | undefined {
  const additionalFromForm = (formData.additionalDetails ?? '').trim();
  if (workType === RetainerWorkType.SomethingElse) {
    const workDescription = getWorkDescription(formData);
    const merged = [workDescription, additionalFromForm]
      .filter(Boolean)
      .join('\n\n');
    return merged.length > 0 ? merged : undefined;
  }
  if (additionalFromForm.length > 0) {
    return additionalFromForm;
  }
  return undefined;
}

function buildAdditionalDetailsForFlat(
  formData: RequestRetainerValidatedFormData,
): string {
  const additionalFromForm = (formData.additionalDetails ?? '').trim();
  if (additionalFromForm.length > 0) {
    return additionalFromForm;
  }
  const workDescription = getWorkDescription(formData);
  if (workDescription.length > 0) {
    return workDescription;
  }
  const label = getWorkTypeLabel(formData);
  if (label.length > 0) {
    return label;
  }
  return formData.title.trim();
}

export interface MapCreateRetainerRequestInputParams {
  matterExpertId: string;
  formData: RequestRetainerValidatedFormData;
  /** Effective hourly rate in dollars (from fee schedule or custom), for hourly billing only. */
  hourlyRateDollars: number;
}

/**
 * Builds GraphQL input for `createRetainerRequest`. Amounts are **cents** (server computes totals).
 */
export function mapRequestRetainerFormToCreateInput({
  matterExpertId,
  formData,
  hourlyRateDollars,
}: MapCreateRetainerRequestInputParams): CreateRetainerRequestInput {
  const workType = formData.workType;
  const feeBreakdownType = resolveFeeBreakdownType(formData);
  const isProBono = formData.feeType === RetainerFeeBreakdownType.ProBono;
  const isFlatRate = formData.billingType === BILLING_TYPE_FLAT;

  if (isFlatRate) {
    const flatFeeCents = Math.round(formData.flatAmount * CENTS_PER_DOLLAR);
    return {
      matterExpertId,
      title: formData.title.trim(),
      workType,
      feeBreakdownType,
      feeType: RetainerFeeType.FlatRate,
      flatFee: isProBono ? 0 : flatFeeCents,
      additionalDetails: buildAdditionalDetailsForFlat(formData),
    };
  }

  const hourlyFeeCents = Math.round(hourlyRateDollars * CENTS_PER_DOLLAR);
  const additionalDetails = buildAdditionalDetailsForHourly(formData, workType);

  return {
    matterExpertId,
    title: formData.title.trim(),
    workType,
    feeBreakdownType,
    feeType: RetainerFeeType.Hourly,
    hourlyFee: isProBono ? 0 : hourlyFeeCents,
    hoursRequested: String(formData.hours),
    additionalDetails,
  };
}
